import type { AgreementBlock, AgreementDocument } from "./document";
import { formatAgreementDate } from "./document";
import { USER_AGREEMENT } from "./userAgreement";

export type DamagePolicy = "ownerWaives" | "borrowerPays";

export const DAMAGE_POLICY_LABELS: Record<DamagePolicy, string> = {
  ownerWaives: "Owner waives damage to the boat",
  borrowerPays: "Borrower pays for damage to the boat",
};

export interface BoatLoanDetails {
  boatName: string;
  /** e.g. "Diamondback, 15 ft"; omitted when unknown. */
  boatDescription: string | undefined;
  ownerName: string;
  borrowerName: string;
  borrowerEmail: string;
  startDate: string;
  endDate: string;
  partySize: number;
  operatorNames: string;
  damagePolicy: DamagePolicy;
}

/** Increment when the wording below changes. */
export const BOAT_LOAN_AGREEMENT_VERSION =
  "v1 (2026-10-02, draft pending attorney review)";

const damageBlocks = (policy: DamagePolicy): AgreementBlock[] =>
  policy === "ownerWaives"
    ? [
        {
          type: "paragraph",
          strong: true,
          text: "7.1 For this trip the Owner accepts the risk of loss or damage to the loaned boat and its equipment, and will not seek payment from the Borrower to repair or replace it.",
        },
        {
          type: "paragraph",
          text: "7.2 This waiver does not apply if the damage happened while the Borrower or anyone they allowed aboard was: (a) operating under the influence of alcohol or drugs; (b) letting someone other than an approved operator operate the boat; (c) using the boat for any illegal purpose or for payment; or (d) acting intentionally or recklessly.",
        },
      ]
    : [
        {
          type: "paragraph",
          strong: true,
          text: "7.1 For this trip the Borrower is responsible for the reasonable cost to repair the loaned boat, or its fair market value if it cannot be repaired, less any amount the Owner's insurer pays.",
        },
      ];

/** The Boat Loan Agreement for one reservation, built identically in the browser and on the server. */
export const buildBoatLoanAgreement = (
  d: BoatLoanDetails
): AgreementDocument => ({
  kind: "boatLoan",
  version: BOAT_LOAN_AGREEMENT_VERSION,
  title: "VetBoats Boat Loan Agreement",
  notice:
    "PLEASE READ CAREFULLY. THIS AGREEMENT COVERS ONE SPECIFIC TRIP AND GIVES UP LEGAL RIGHTS, INCLUDING YOUR RIGHT TO SUE FOR INJURY, DEATH, OR PROPERTY DAMAGE.",
  facts: [
    {
      label: "Boat",
      value: d.boatDescription
        ? `${d.boatName} (${d.boatDescription})`
        : d.boatName,
    },
    { label: "Owner", value: d.ownerName },
    { label: "Borrower", value: `${d.borrowerName} (${d.borrowerEmail})` },
    {
      label: "Dates",
      value:
        d.startDate === d.endDate
          ? formatAgreementDate(d.startDate)
          : `${formatAgreementDate(d.startDate)} through ${formatAgreementDate(d.endDate)}`,
    },
    { label: "Party size", value: String(d.partySize) },
    { label: "Approved operators", value: d.operatorNames },
    {
      label: "Damage to the loaned boat",
      value: DAMAGE_POLICY_LABELS[d.damagePolicy],
    },
  ],
  sections: [
    {
      heading: "1. This trip",
      blocks: [
        {
          type: "paragraph",
          text: `1.1 This Boat Loan Agreement covers the trip described above. It is part of the ${USER_AGREEMENT.title} (${USER_AGREEMENT.version}), which the Borrower has signed. Words with capital letters have the meanings given there, and if the two conflict about this trip, this agreement controls.`,
        },
        {
          type: "paragraph",
          text: "1.2 The loan is free. No money or other payment is exchanged for the use of the boat. The Borrower may replace fuel they used.",
        },
      ],
    },
    {
      heading: "2. Risks the Borrower accepts",
      blocks: [
        {
          type: "paragraph",
          text: "2.1 Boating is inherently dangerous and can cause serious injury, drowning, death, and property loss, including from capsizing, sinking, collision, fire, carbon monoxide, weather, equipment failure, hidden defects, other boaters, and delayed rescue.",
        },
        {
          type: "paragraph",
          strong: true,
          text: "2.2 The Borrower knowingly and voluntarily assumes all of these risks, known and unknown, including risks arising from the ordinary negligence of any Released Party.",
        },
      ],
    },
    {
      heading: "3. Release and waiver",
      blocks: [
        {
          type: "paragraph",
          strong: true,
          text: "3.1 To the fullest extent allowed by law, the Borrower releases, waives, and promises not to sue any Released Party for any injury, illness, death, property damage, or other loss arising from this trip or the use of this boat, including losses caused by the ordinary negligence of a Released Party.",
        },
        {
          type: "paragraph",
          text: "3.2 This release does not cover a Released Party's gross negligence, recklessness, or intentional misconduct, or anything else the law does not allow to be waived.",
        },
      ],
    },
    {
      heading: "4. Who may operate the boat",
      blocks: [
        {
          type: "paragraph",
          text: "4.1 Only the approved operators listed above may operate the boat. The Borrower confirms that each of them holds every boating license and safety certificate required where the boat will be used, and will not operate it under the influence of alcohol or drugs.",
        },
      ],
    },
    {
      heading: "5. Everyone aboard must sign",
      blocks: [
        {
          type: "paragraph",
          strong: true,
          text: "5.1 Getting everyone aboard to sign is the Borrower's responsibility alone. Before departure, the Borrower must make sure that every adult aboard has signed the User Agreement using this trip's guest waiver link, and that a parent or legal guardian has signed it for every minor aboard. VetBoats and the Owner have no duty to check. If anyone boards without signing, the Borrower is fully responsible for, and will defend, indemnify, and hold harmless every Released Party against, any claim by or through that person.",
        },
      ],
    },
    {
      heading: "6. Borrower pays for harm to others",
      blocks: [
        {
          type: "paragraph",
          strong: true,
          text: "6.1 The Borrower is fully responsible for, and will pay, all injury, death, and damage to any person or property other than the loaned boat itself that happens while the boat is in the Borrower's control, including injury to Guests, other boaters, and swimmers; damage to docks and property; fines; fuel spills and pollution cleanup; and the cost of towing, salvage, raising, and removing the boat if it becomes disabled, grounded, or sinks.",
        },
        {
          type: "paragraph",
          text: "6.2 The Borrower will defend, indemnify, and hold harmless every Released Party from any claim, demand, lawsuit, cost, or expense (including reasonable attorney fees) arising from the Borrower's or any Guest's use of the boat.",
        },
      ],
    },
    {
      heading: "7. Damage to the loaned boat itself",
      blocks: [
        ...damageBlocks(d.damagePolicy),
        {
          type: "paragraph",
          text: "7.3 Nothing in this Section 7 limits the Borrower's responsibility under Section 6 for harm to anyone or anything other than the loaned boat.",
        },
      ],
    },
    {
      heading: "8. Electronic signature",
      blocks: [
        {
          type: "paragraph",
          text: '8.1 Checking the box, typing your full legal name, and selecting "Sign" is your electronic signature and has the same effect as a handwritten signature. VetBoats keeps a permanent record of it, including the exact text above, and emails you a copy.',
        },
        {
          type: "paragraph",
          strong: true,
          text: "8.2 BY SIGNING, THE BORROWER CONFIRMS THEY HAVE READ THIS AGREEMENT, UNDERSTAND IT, ARE GIVING UP SUBSTANTIAL LEGAL RIGHTS, AND SIGN IT FREELY.",
        },
      ],
    },
  ],
});

interface LoanBoat {
  name: string;
  makeModel: string | undefined;
  lengthFeet?: number | undefined;
  ownerName: string;
  damagePolicy: DamagePolicy;
}

interface LoanTrip {
  startDate: string;
  endDate: string;
  partySize: number;
  operatorNames: string;
}

/** Assembles loan details the same way in the browser and on the server, so the text matches exactly. */
export const boatLoanDetailsFor = (
  boat: LoanBoat,
  borrower: { displayName: string; email: string },
  trip: LoanTrip
): BoatLoanDetails => {
  const description = [
    boat.makeModel,
    boat.lengthFeet !== undefined ? `${boat.lengthFeet} ft` : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(", ");
  return {
    boatName: boat.name,
    boatDescription: description || undefined,
    ownerName: boat.ownerName,
    borrowerName: borrower.displayName,
    borrowerEmail: borrower.email,
    startDate: trip.startDate,
    endDate: trip.endDate,
    partySize: trip.partySize,
    operatorNames: trip.operatorNames.trim(),
    damagePolicy: boat.damagePolicy,
  };
};
